import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConsentChoice,
  getStoredConsent,
  isConsentRequiredRegion,
  setConsent,
} from '../lib/analytics';
import { cn } from '../lib/utils';

/**
 * Event that reopens the banner, so a visitor can change their mind from the
 * footer link. Withdrawing consent has to be as easy as giving it.
 */
const OPEN_EVENT = 'centmap:cookie-settings';

export function openCookieSettings(): void {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

/**
 * Cookie consent banner, shown to visitors in the EEA and the UK.
 *
 * Elsewhere analytics storage is granted by the Consent Mode default in
 * index.html and no banner appears — but the footer link still reopens this
 * one, so anyone anywhere can opt out.
 */
export const CookieConsent: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (getStoredConsent() === null && isConsentRequiredRegion()) {
      setIsOpen(true);
    }
  }, []);

  useEffect(() => {
    const reopen = () => setIsOpen(true);
    window.addEventListener(OPEN_EVENT, reopen);
    return () => window.removeEventListener(OPEN_EVENT, reopen);
  }, []);

  const choose = useCallback((choice: ConsentChoice) => {
    setConsent(choice);
    setIsOpen(false);
  }, []);

  if (!isOpen) return null;

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className={cn(
        'fixed inset-x-0 bottom-0 z-[60]',
        'border-t border-gray-200 bg-white shadow-[0_-4px_16px_rgba(0,0,0,0.08)]'
      )}
    >
      <div
        className={cn(
          'mx-auto flex max-w-4xl flex-col gap-4 px-4 py-4',
          'sm:flex-row sm:items-center sm:justify-between sm:gap-6'
        )}
      >
        <p className="text-sm leading-relaxed text-gray-600">
          We use Google Analytics cookies to see how people find and use
          Centmap. We only set them if you agree.{' '}
          <Link
            to="/privacy-policy"
            className="text-[#167062] underline hover:text-[#0f524a]"
          >
            Privacy Policy
          </Link>
        </p>

        <div className="flex flex-shrink-0 gap-3">
          <button
            type="button"
            onClick={() => choose('denied')}
            className={cn(
              'flex-1 whitespace-nowrap rounded-lg px-4 py-2 sm:flex-none',
              'border border-gray-300 text-sm font-medium text-gray-700',
              'transition-colors hover:bg-gray-50'
            )}
          >
            Decline
          </button>
          <button
            type="button"
            onClick={() => choose('granted')}
            className={cn(
              'flex-1 whitespace-nowrap rounded-lg px-4 py-2 sm:flex-none',
              'bg-gray-900 text-sm font-medium text-white',
              'transition-colors hover:bg-gray-800'
            )}
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};
